import { useQuery } from "@tanstack/react-query";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { get } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import type { LinkedInEvent } from "../types";

async function getUserEventsRequest(
  workspaceId: string,
  campaignId: string,
): Promise<LinkedInEvent[]> {
  return (await get(`workspaces/${workspaceId}/campaigns/${campaignId}/events`))
    .events;
}

async function getLinkedInEventRequest(
  workspaceId: string,
  campaignId: string,
  eventId: string,
): Promise<LinkedInEvent> {
  return (
    await get(
      `workspaces/${workspaceId}/campaigns/${campaignId}/events/${eventId}`,
    )
  ).event;
}

export default function useLinkedInEvents({
  eventId,
  disableQuery,
}: {
  eventId?: string;
  disableQuery?: boolean;
} = {}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { id: campaignId, miner } = useCampaignContext();
  const { campaignsKeys } = getQueryKeys(workspaceId);

  const {
    data: linkedInEvents,
    isLoading: isLoadingLinkedInEvents,
    refetch: refetchLinkedInEvents,
  } = useQuery({
    queryKey: campaignsKeys.linkedInEvents(campaignId, miner),
    queryFn: () => getUserEventsRequest(workspaceId, campaignId),
    enabled: !eventId && !disableQuery,
  });

  const { data: linkedInEvent, isLoading: isLoadingLinkedInEvent } = useQuery({
    queryKey: campaignsKeys.linkedInEvent(campaignId, eventId),
    queryFn: () => getLinkedInEventRequest(workspaceId, campaignId, eventId),
    enabled: !!eventId && !disableQuery,
  });

  return {
    linkedInEvents,
    isLoadingLinkedInEvents,
    linkedInEvent,
    isLoadingLinkedInEvent,
    refetchLinkedInEvents,
  };
}
